import React from 'react'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { Link, useParams } from 'react-router-dom'
import CopyableValue from '../../../../components/CopyableValue'
import DisplayField from '../../../../components/Form/DisplayField'
import InlineEditInput from '../../../../components/Form/InlineEditInput'
import Panel from '../../../../components/Panel'
import { updateTenantAccountNameApi } from '../../../../apis/apis'
import { getTenant } from '../Tenants.slice'
import { useAppDispatch, useAppSelector } from '../../../../shared/redux/hooks'
import { ServiceAccountWithInvoices } from './accountStatusUtils'

interface TabGeneralProps {
  title: string
  data: ServiceAccountWithInvoices
}

const SecondaryCuid: React.FC<{ value?: string | null; label: string }> = ({ value, label }) => {
  if (value == null || value === '') {
    return <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">Cuid not set</div>
  }

  return (
    <div className="mt-0.5 min-w-0">
      <CopyableValue
        value={value}
        label={label}
        valueClassName="font-mono text-xs text-slate-500 dark:text-slate-400"
        iconClassName="h-3.5 w-3.5"
      />
    </div>
  )
}

const TabGeneral: React.FC<TabGeneralProps> = (props) => {
  const { data } = props
  const dispatch = useAppDispatch()
  const { tenantId } = useParams()
  const { permissions } = useAppSelector((state) => state.app)

  const hasNameModifyPermission = permissions?.includes('account:manager:name:modify') ?? false
  const utilityLabel = data.utilityName || 'Utility'

  return (
    <div className="space-y-4">
      <Panel header="OneBill™">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {hasNameModifyPermission ? (
            <InlineEditInput
              name="kingAccountName"
              label="Account name"
              required
              initialValue={data.kingAccountName}
              handleSubmit={async (editedValue) => {
                if (editedValue) {
                  await updateTenantAccountNameApi(data.id, editedValue)
                  await dispatch(getTenant(tenantId as string))
                }
              }}
            />
          ) : (
            <DisplayField label="Account name">{data.kingAccountName}</DisplayField>
          )}
          <DisplayField label="Account number">
            <div className="min-w-0 space-y-0.5">
              <CopyableValue
                value={data.kingAccountNumber}
                label="Account number"
                valueClassName="font-mono text-sm"
                iconClassName="h-4 w-4"
              />
              <SecondaryCuid value={data.id} label="tenant account cuid" />
            </div>
          </DisplayField>
          <DisplayField label="Program">
            {(data.programs?.length ?? 0) > 0 ? (
              <div className="flex flex-col gap-2">
                {data.programs!.map((program) => (
                  <div key={program.id} className="min-w-0">
                    <Link
                      to={`/program/programs/${program.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-medium text-slate-800 underline transition-colors hover:text-indigo-700 dark:text-slate-100 dark:hover:text-indigo-300"
                    >
                      {program.name || program.id}
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </Link>
                    {program.name ? <SecondaryCuid value={program.id} label="program cuid" /> : null}
                  </div>
                ))}
              </div>
            ) : (
              <span>Not set</span>
            )}
          </DisplayField>
        </div>
      </Panel>

      <Panel header={utilityLabel}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <DisplayField label="Account name">{data.utilityAccountName}</DisplayField>
          <DisplayField label="Account number">
            <div className="min-w-0 space-y-0.5">
              <CopyableValue
                value={data.utilityAccountNumber}
                label={`${utilityLabel} account number`}
                valueClassName="font-mono text-sm"
                iconClassName="h-4 w-4"
              />
              <SecondaryCuid value={data.utilityAccountCuid} label="utility account cuid" />
            </div>
          </DisplayField>
          <DisplayField label="Billing address">
            {data.billingAddress != null ? (
              <span>
                {data.billingAddress.street1} {data.billingAddress.street2}
                <br />
                {data.billingAddress.city}, {data.billingAddress.state} {data.billingAddress.zip}
              </span>
            ) : (
              <span>Not set</span>
            )}
          </DisplayField>
        </div>
      </Panel>
    </div>
  )
}

export default TabGeneral
